import type { ColDef } from 'ag-grid-community'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { formatCalendarDate } from '@sportninja/common/ts-utils/date-utils'
import PlayerNameCell from '../components/cells/PlayerNameCell'
import PlayerStatusCell, {
  type PlayerStatusCellParams,
} from '../components/cells/PlayerStatusCell'
import type { GameRosterPlayer, RosterTypeOption } from '../types'
import { rosterComparator } from '../utils/rosterSort'

interface Params extends PlayerStatusCellParams {
  playerTypes: RosterTypeOption[]
  // Embedded (iframe) roster: no DOB column.
  isInFrame: boolean
}

/** Columns of the Players / Spares grids: name, #, position, DOB, status. */
export const usePlayerColumns = ({
  playerTypes,
  isInFrame,
  ...statusParams
}: Params): ColDef<GameRosterPlayer>[] => {
  'use memo'

  const columns: ColDef<GameRosterPlayer>[] = [
    {
      colId: 'name',
      headerName: capitalizeWords(t('common:name', {})),
      flex: 3,
      minWidth: 220,
      sortable: true,
      initialSort: 'asc',
      comparator: rosterComparator<GameRosterPlayer>('name_last'),
      cellRenderer: PlayerNameCell,
    },
    {
      colId: 'player_number',
      headerName: '#',
      width: 88,
      minWidth: 88,
      sortable: true,
      comparator: rosterComparator<GameRosterPlayer>('player_number'),
      valueGetter: ({ data }) => data?.player_number || '-',
    },
    {
      colId: 'position',
      headerName: capitalizeWords(t('common:position', {})),
      flex: 2,
      minWidth: 160,
      sortable: true,
      comparator: rosterComparator<GameRosterPlayer>('player_type.id'),
      valueGetter: ({ data }) =>
        playerTypes.find(
          (type) => String(type.id) === String(data?.player_type?.id)
        )?.name_full || '-',
    },
  ]

  if (!isInFrame) {
    columns.push({
      colId: 'birth_date',
      headerName: 'DOB',
      flex: 2,
      minWidth: 128,
      valueGetter: ({ data }) => formatCalendarDate(data?.birth_date),
    })
  }

  columns.push({
    colId: 'status',
    headerName: capitalizeWords('Status'),
    flex: 3,
    minWidth: 240,
    cellRenderer: PlayerStatusCell,
    cellRendererParams: statusParams,
  })

  return columns
}
